import { useState, type ReactNode } from "react";
import {
  Activity,
  Banknote,
  CircleDollarSign,
  FileClock,
  Gift,
  Globe2,
  LayoutDashboard,
  LogOut,
  Menu,
  Mail,
  Settings,
  ShieldCheck,
  Users,
  UserCog,
  Wrench,
  ClipboardList,
  PanelsTopLeft,
  BellRing,
  X
} from "lucide-react";
import type { PortalView, Viewer } from "../types";
import { useTimeZone } from "../lib/TimeZoneContext";
import { timeZoneLabel } from "../lib/timeZone";
import { DEPLOYMENT_LABEL } from "../lib/runtime";
import { canAccessView, roleLabel } from "../lib/roles";

interface AppShellProps {
  children: ReactNode;
  viewer: Viewer;
  activeView: PortalView;
  onNavigate: (view: PortalView) => void;
  onLogout: () => void;
}

const navigationGroups: Array<{ label: string; items: Array<{ label: string; icon: typeof LayoutDashboard; view: PortalView }> }> = [
  { label: "Workspace", items: [
    { label: "Overview", icon: LayoutDashboard, view: "overview" },
    { label: "Feature & bug reports", icon: ClipboardList, view: "tickets" },
    { label: "Ticket email settings", icon: Mail, view: "ticket-email-settings" }
  ] },
  { label: "Customer operations", items: [
    { label: "Users", icon: Users, view: "users" },
    { label: "KYC & compliance", icon: ShieldCheck, view: "kyc" },
    { label: "Transactions", icon: Banknote, view: "transactions" },
    { label: "Gift lifecycle", icon: Gift, view: "cards" },
    { label: "Notifications", icon: BellRing, view: "notifications" }
  ] },
  { label: "Monitoring", items: [
    { label: "Logs", icon: Activity, view: "logs" },
    { label: "Crashes", icon: Activity, view: "crashes" },
    { label: "Infrastructure costs", icon: CircleDollarSign, view: "costs" },
    { label: "Audit trail", icon: FileClock, view: "audit" }
  ] },
  { label: "Administration", items: [
    { label: "Card templates", icon: PanelsTopLeft, view: "card-templates" },
    { label: "App controls", icon: Wrench, view: "controls" },
    { label: "Staff roles", icon: UserCog, view: "staff" },
    { label: "Settings", icon: Settings, view: "settings" }
  ] }
];

export function AppShell({ children, viewer, activeView, onNavigate, onLogout }: AppShellProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { timeZone, localTimeZone, options, setTimeZone } = useTimeZone();
  const groups = navigationGroups.map((group) => ({ ...group, items: group.items.filter((item) => canAccessView(viewer.role, item.view)) })).filter((group) => group.items.length);

  return (
    <div className="app-shell">
      <aside className={`sidebar ${menuOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-topline">
          <div className="brand-lockup"><span className="brand-mark" aria-hidden="true">O</span><span>OTTAGIVE</span></div>
          <button className="mobile-close" onClick={() => setMenuOpen(false)} aria-label="Close navigation"><X size={20} /></button>
        </div>
        <div className="workspace-label"><span>{viewer.role === "cardTemplateCreator" ? "Template workspace" : "Admin workspace"}</span><span className="dev-pill">{DEPLOYMENT_LABEL}</span></div>
        <nav aria-label="Primary navigation">
          {groups.map((group) => <div className="nav-group" key={group.label}>
            <p>{group.label}</p>
            {group.items.map(({ label, icon: Icon, view }) => {
              const active = view === activeView || (view === "users" && activeView === "customer") || (view === "cards" && activeView === "legacy-cards");
              return <button key={label} className={`${active ? "nav-item active" : "nav-item"}${view === "crashes" ? " nav-subitem" : ""}`} aria-current={active ? "page" : undefined} onClick={() => { onNavigate(view); setMenuOpen(false); }}>
                <Icon size={18} aria-hidden="true" /><span>{label}</span>
              </button>;
            })}
          </div>)}
        </nav>
        <div className="sidebar-footer">
          <label className="timezone-control">
            <span><Globe2 size={15} aria-hidden="true" /> Display timezone</span>
            <select value={timeZone} onChange={(event) => setTimeZone(event.target.value)} aria-label="Display timezone">
              {options.map((value) => <option key={value} value={value}>{timeZoneLabel(value, localTimeZone)}</option>)}
            </select>
          </label>
          <div className="account-card">
            <span className="avatar">{viewer.email.charAt(0).toUpperCase()}</span>
            <div><strong>{viewer.email}</strong><span>{roleLabel(viewer.role)}</span></div>
            <button onClick={onLogout} aria-label="Sign out"><LogOut size={17} /></button>
          </div>
        </div>
      </aside>
      {menuOpen ? <button className="sidebar-scrim" onClick={() => setMenuOpen(false)} aria-label="Close navigation" /> : null}
      <main className="main-content">
        <div className="mobile-header">
          <button className="icon-button" onClick={() => setMenuOpen(true)} aria-label="Open navigation"><Menu size={20} /></button>
          <div className="brand-lockup"><span className="brand-mark">O</span><span>OTTAGIVE</span></div>
          <span className="dev-pill">{DEPLOYMENT_LABEL}</span>
        </div>
        {children}
      </main>
    </div>
  );
}
